import moment from 'moment';

import {
  CALENDAR_ROW_DURATION,
  CALENDAR_TIME_UNIT,
  DocumentMapEnum,
  ROW_HEIGHT,
  TIME_UNIT_HEIGHT,
} from './constants';
import {
  BTSessionStatus,
  AssessmentNoteStatus,
  ENTITY_PREFIX,
  SupervisionNoteStatus,
  SupervisionType,
} from '../../../constants';

import type { Moment } from 'moment';
import type {
  IBtSupervision,
  IClientAssessment,
  IClientSupervision,
  IGroupSupervision,
  IParentSupervision,
  ISessionMinified,
} from '../../../utils/types';

export function initializeCalendarData() {
  return {
    [DocumentMapEnum.ASSESSMENT]: [],
    [DocumentMapEnum.BT_SN]: [],
    [DocumentMapEnum.CLIENT_SN]: [],
    [DocumentMapEnum.GROUP_TRAINING]: [],
    [DocumentMapEnum.PARENT_SN]: [],
    [DocumentMapEnum.SESSION]: [],
  };
}

export function getUnits(
  windowStartTime: Moment,
  windowEndTime: Moment,
  resourceDateStart: Moment,
  duration: number,
  usePartialDuration: boolean,
  isFirstRow: boolean,
) {
  const resourceDateEnd = moment(resourceDateStart).add(duration, 'seconds');
  if (!usePartialDuration || (isFirstRow && resourceDateEnd.isAfter(windowEndTime))) {
    return duration / CALENDAR_TIME_UNIT;
  }
  // Handle condition when doc date start is before windowStartTime
  if (resourceDateStart.isBefore(windowStartTime)) {
    const diffSec = moment(windowStartTime).diff(resourceDateStart) / 1000;
    return (duration - diffSec) / CALENDAR_TIME_UNIT;
  }

  // Handle Condition when doc date start and end lies within start and end time
  if (
    resourceDateStart.isSameOrAfter(windowStartTime) &&
    resourceDateEnd.isSameOrBefore(windowEndTime)
  ) {
    return duration / CALENDAR_TIME_UNIT;
  }

  // Handle Condition when doc date end is after endTime
  if (resourceDateEnd.isAfter(windowEndTime)) {
    const diffSec = moment(resourceDateEnd).diff(windowEndTime) / 1000;
    return (duration - diffSec) / CALENDAR_TIME_UNIT;
  }
  return duration / CALENDAR_TIME_UNIT;
}

export const getTopValue = (windowStartTime: Moment, resourceStartTime: Moment) => {
  if (resourceStartTime.isSameOrAfter(windowStartTime)) {
    const diffSec = Math.abs(windowStartTime.diff(resourceStartTime)) / 1000;
    const units = diffSec / CALENDAR_TIME_UNIT;
    return units * TIME_UNIT_HEIGHT;
  }
  if (resourceStartTime.isBefore(windowStartTime)) {
    return 0;
  }
};

type DocumentResource =
  | IClientAssessment
  | IBtSupervision
  | IClientSupervision
  | IGroupSupervision
  | IParentSupervision
  | ISessionMinified;

export const prepareDocumentDataByWeek = <T extends DocumentResource>(
  week: Moment[],
  resources: T[],
) => {
  const [sun, mon, tue, wed, thu, fri, sat] = week;
  const sunData: T[] = [];
  const monData: T[] = [];
  const tueData: T[] = [];
  const wedData: T[] = [];
  const thuData: T[] = [];
  const friData: T[] = [];
  const satData: T[] = [];

  resources?.forEach((resource) => {
    const durationSeconds =
      'duration' in resource && resource.duration !== ''
        ? Number(resource.duration)
        : 'expected_duration' in resource
          ? resource.expected_duration
          : 0;
    const resourceEndDate = moment(resource.date_start).add(durationSeconds, 'seconds');
    if (
      (moment(resource.date_start).isAfter(sun) && moment(resource.date_start).isBefore(mon)) ||
      (moment(resource.date_start).isBefore(sun) && moment(resourceEndDate).isAfter(sun))
    ) {
      sunData.push(resource);
    }
    if (
      (moment(resource.date_start).isAfter(mon) && moment(resource.date_start).isBefore(tue)) ||
      (moment(resource.date_start).isBefore(mon) && moment(resourceEndDate).isAfter(mon))
    ) {
      monData.push(resource);
    }
    if (
      (moment(resource.date_start).isAfter(tue) && moment(resource.date_start).isBefore(wed)) ||
      (moment(resource.date_start).isBefore(tue) && moment(resourceEndDate).isAfter(tue))
    ) {
      tueData.push(resource);
    }
    if (
      (moment(resource.date_start).isAfter(wed) && moment(resource.date_start).isBefore(thu)) ||
      (moment(resource.date_start).isBefore(wed) && moment(resourceEndDate).isAfter(wed))
    ) {
      wedData.push(resource);
    }
    if (
      (moment(resource.date_start).isAfter(thu) && moment(resource.date_start).isBefore(fri)) ||
      (moment(resource.date_start).isBefore(thu) && moment(resourceEndDate).isAfter(thu))
    ) {
      thuData.push(resource);
    }
    if (
      (moment(resource.date_start).isAfter(fri) && moment(resource.date_start).isBefore(sat)) ||
      (moment(resource.date_start).isBefore(fri) && moment(resourceEndDate).isAfter(fri))
    ) {
      friData.push(resource);
    }
    if (
      (moment(resource.date_start).isAfter(sat) &&
        moment(resource.date_start).isBefore(moment(sat).add(1, 'days'))) ||
      (moment(resource.date_start).isBefore(sat) && moment(resourceEndDate).isAfter(sat))
    ) {
      satData.push(resource);
    }
  });

  return {
    sunData,
    monData,
    tueData,
    wedData,
    thuData,
    friData,
    satData,
  };
};

export const isEventWithinTimeFrame = (resource: any, startTime: Moment, endTime: Moment) => {
  return (
    moment(resource.date_start).isSameOrAfter(startTime) &&
    moment(resource.date_start).isBefore(endTime)
  );
};

export function isDocumentNotConcluded(
  type: SupervisionType | 'session',
  status: SupervisionNoteStatus | BTSessionStatus | AssessmentNoteStatus | undefined,
) {
  if (type === 'session') {
    return (
      status === BTSessionStatus.Started || status === BTSessionStatus.AwaitingGuardianSignature
    );
  }
  return status !== SupervisionNoteStatus.Completed;
}

export function getOverlapRange(
  range1: { start: Moment; end: Moment },
  range2: { start: Moment; end: Moment },
) {
  const start1 = moment(range1.start);
  const end1 = moment(range1.end);
  const start2 = moment(range2.start);
  const end2 = moment(range2.end);

  // Check if the ranges overlap
  if (start1.isBefore(end2) && start2.isBefore(end1)) {
    // Calculate the overlap range
    const overlapStart = moment.max(start1, start2);
    const overlapEnd = moment.min(end1, end2);
    return {
      overlap: true,
      range: { start: overlapStart, end: overlapEnd },
    };
  }

  return { overlap: false, range: null };
}

// Checks if event started previously and ends withing given time frame
export const pastEventEndsInTimeFrame = (resource: any, startTime: Moment) => {
  return (
    moment(resource.date_start).isSameOrBefore(startTime) &&
    moment(resource.date_start)
      .add(resource.duration ?? resource.expected_duration, 'seconds')
      .isAfter(startTime)
  );
};

export const getTimeLabel = (resource: any) => {
  const { date_start } = resource;
  const duration = resource.duration ? resource.duration : resource.expected_duration;
  return `${moment(date_start).format('h:mm a')} to ${moment(date_start)
    .add(duration, 'seconds')
    .format('hh:mm a')}`;
};

export const getTimeTopValue = (startTime: Moment) => {
  const rowDurationInMin = CALENDAR_ROW_DURATION * 60;
  const timeElaspedInMin = moment().diff(moment(startTime)) / 60000;
  const heightPerMinute = ROW_HEIGHT / rowDurationInMin;
  return timeElaspedInMin * heightPerMinute;
};

export const getCalendarEntityPrefix = (type: SupervisionType | 'session') => {
  switch (type) {
    case SupervisionType.Assessment:
    case SupervisionType.Assessment2:
      return ENTITY_PREFIX.ClientAssessmentNote;
    case SupervisionType.Bt:
      return ENTITY_PREFIX.BTSupervisionNotes;
    case SupervisionType.Client:
      return ENTITY_PREFIX.ClientSupervisionNotes;
    case SupervisionType.Parent:
      return ENTITY_PREFIX.ParentSupervisionNotes;
    case SupervisionType.GroupParent:
      return ENTITY_PREFIX.GroupSupervisionNotes;
    case 'session':
      return ENTITY_PREFIX.SessionNotes;
  }
};
